import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSwipeGesture } from '../hooks/useSwipeGesture';
import { keyframes } from '@mui/system';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon, CloudQueue as CloudIcon, Phone as PhoneIcon, Email as EmailIcon, WorkspacePremium as WorkspacePremiumIcon } from '@mui/icons-material';

const pulsate = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(237, 108, 2, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(237, 108, 2, 0.7);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(237, 108, 2, 0.4);
  }
`;

type MenuItem = 
  | { label: string; section: string; route?: never }
  | { label: string; route: string; section?: never };

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  
  const swipeHandlers = useSwipeGesture({
    onSwipeRight: () => {
      // Only open drawer if swipe starts from left edge (within 50px)
      if (window.innerWidth > 0) {
        setMobileOpen(true);
      }
    },
    onSwipeLeft: () => setMobileOpen(false),
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (section: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const menuItems: MenuItem[] = [
    { label: 'Home', section: 'home' },
    { label: 'Services', section: 'services' },
    { label: 'About', section: 'about' },
    { label: 'Success Stories', route: '/success-stories' },
    { label: 'Blog', section: 'blog' },
    { label: 'Contact', section: 'contact' },
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box 
        sx={{ my: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
        onClick={(e) => {
          e.stopPropagation();
          navigate('/');
          window.scrollTo(0, 0);
          handleDrawerToggle();
        }}
      >
        <CloudIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6">
          TNCOSOFT
        </Typography>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem 
            key={item.label} 
            disablePadding 
            onClick={(e) => {
              e.stopPropagation();
              if (item.route) {
                navigate(item.route);
              } else if (item.section) {
                handleNavigation(item.section);
              }
              handleDrawerToggle();
            }}
            sx={{ cursor: 'pointer' }}
          >
            <ListItemText primary={item.label} sx={{ textAlign: 'center' }} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {/* Top Bar */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)', 
        color: 'white', 
        py: 1.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}>
        <Box sx={{ 
          maxWidth: 'lg', 
          mx: 'auto', 
          px: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap' 
        }}>
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' }, 
            alignItems: 'center', 
            gap: 1.5,
            bgcolor: 'rgba(255,255,255,0.1)',
            px: 2,
            py: 0.5,
            borderRadius: 2
          }}>
            <WorkspacePremiumIcon sx={{ fontSize: '1.2rem', color: '#ffd700' }} />
            <Typography variant="body2" sx={{ 
              fontSize: { xs: '0.85rem', md: '0.95rem' },
              fontWeight: 600,
              letterSpacing: '0.5px'
            }}>
              ISO 9001:2015 Certified Company
            </Typography>
          </Box>
          <Box sx={{ 
            display: { xs: 'flex', sm: 'none' }, 
            alignItems: 'center', 
            gap: 0.5,
            bgcolor: 'rgba(255,255,255,0.1)',
            px: 1,
            py: 0.3,
            borderRadius: 1
          }}>
            <WorkspacePremiumIcon sx={{ fontSize: '1rem', color: '#ffd700' }} />
            <Typography variant="caption" sx={{ 
              fontSize: '0.7rem',
              fontWeight: 600
            }}>
              ISO Certified
            </Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 4, alignItems: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.8,
              '&:hover': { opacity: 0.8 },
              cursor: 'pointer'
            }}>
              <PhoneIcon sx={{ fontSize: '1.1rem' }} />
              <Typography variant="body2" sx={{ 
                fontSize: '0.95rem',
                fontWeight: 500
              }}>
                +91-8999096807
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.8,
              '&:hover': { opacity: 0.8 },
              cursor: 'pointer'
            }}>
              <EmailIcon sx={{ fontSize: '1.1rem' }} />
              <Typography variant="body2" sx={{ 
                fontSize: '0.95rem',
                fontWeight: 500
              }}>
                njinabad@tncosoft.com
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, alignItems: 'center' }}>
            <PhoneIcon sx={{ fontSize: '1rem' }} />
            <EmailIcon sx={{ fontSize: '1rem' }} />
          </Box>
        </Box>
      </Box>
      
      <AppBar position="sticky" elevation={1}>
        <Toolbar>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexGrow: 1,
              cursor: 'pointer'
            }}
            onClick={() => {
              navigate('/');
              handleNavigation('home');
            }}
          >
            <CloudIcon sx={{ mr: 1, color: 'primary.main', fontSize: '2rem' }} />
            <Typography
              variant="h6"
              component="div"
              sx={{ 
                fontWeight: 800,
                fontSize: '1.5rem',
                letterSpacing: '0.1em',
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
              }}
            >
              TNCOSOFT
            </Typography>
          </Box>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.label}
                  color="inherit"
                  onClick={() => item.route ? navigate(item.route) : handleNavigation(item.section!)}
                  sx={{ color: 'text.primary' }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleNavigation('contact')}
                sx={{ 
                  ml: 2,
                  bgcolor: 'warning.main',
                  color: 'white',
                  fontWeight: 700,
                  animation: `${pulsate} 2s ease-in-out infinite`,
                  '&:hover': {
                    bgcolor: 'warning.dark',
                    animation: 'none',
                    transform: 'scale(1.05)',
                  }
                }}
              >
                Get Quote Now!
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
        PaperProps={{
          onTouchStart: mobileOpen ? swipeHandlers.onTouchStart : undefined,
          onTouchMove: mobileOpen ? swipeHandlers.onTouchMove : undefined,
          onTouchEnd: mobileOpen ? swipeHandlers.onTouchEnd : undefined,
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;